<script setup lang="ts">
import { h, computed } from 'vue'
import { useStore } from 'vuex'
import type { ColumnDef } from '@tanstack/vue-table'
import {formatTime} from '@/utils/datetime'

import { ArrowUpDown, ChevronDown } from 'lucide-vue-next'
import { Button } from '@/components/ui/button'
import { DataTable } from '@/components/ui/data-table'
import { MemberAvatar } from '@/components/ui/avatar'

import { can } from '@/composables/useCan'
import RoleSelect from '@/components/roles/RoleSelect.vue'
import MembersListDropdown from './MembersListDropdown.vue'

import type { Member } from '@/types'

const store = useStore()
const settings = computed(() => store.getters['user/getUserSettings'])
const project = computed(() => store.getters['project/current'])
const canUpdate = computed(() => can('project_member', 'update', {projectId: project.value?.project_id}))

const props = defineProps<{
  members: Member[]
}>()

const changeRole = (member: Member, roleId: number) => store.dispatch('project/setMemberRole', {member, roleId})

const columns = computed<ColumnDef<Member>[]>(() => [
  {
    accessorKey: 'display_name',
    header: ({column}) => {
      return h(Button, {
        variant: 'ghost',
        size: 'sm',
        onClick: () => column.toggleSorting(column.getIsSorted() === 'asc'),
      }, () => ['Name', h(ArrowUpDown, {class: 'ml-1 h-3 w-3'})])
    },
    cell: ({row}) => {
      const member = row.original
      return h(MemberAvatar, {avatar_url: member.avatar_url, display_name: member.display_name, verified: member.verified})
    },
  },
  {
    accessorKey: 'email',
    header: ({ column }) =>
      h(
        Button,
        {
          variant: 'ghost',
          size: 'sm',
          onClick: () => column.toggleSorting(column.getIsSorted() === 'asc'),
        },
        () => ['Contact', h(ArrowUpDown, { class: 'ml-1 h-3 w-3' })]
      ),
    cell: ({ row }) => {
      const { email, phone } = row.original
      return h('div', { class: 'text-muted-foreground' }, [
        h('div', { class: 'lowercase' }, email),
        phone
          ? h('div', { class: 'whitespace-nowrap' }, phone)
          : null,
      ])
    },
  },
  {
    id: 'role',
    header: 'Role',
    cell: ({row}) => {
      const member = row.original
      if (!canUpdate.value) {
        return h('div', {class: 'text-muted-foreground'}, store.getters['access/role'](member.role_id)?.name ?? 'No role')
      }
      return h(RoleSelect, {
        kind: 'project',
        modelValue: member.role_id,
        'onUpdate:modelValue': (roleId: number) => changeRole(member, roleId),
      })
    },
  },
  // {
  //   accessorKey: 'phone',
  //   header: 'Phone',
  //   cell: ({row}) => {
  //     // @ts-ignore
  //     return h('div', {class: 'capitalize text-muted-foreground'}, row.getValue('phone'))
  //   },
  // },
  {
    accessorKey: 'create_dttm',
    header: 'Member since',
    cell: ({row}) => {
      const date = formatTime(row.getValue('create_dttm'), settings.value.tz, 'D MMMM YYYY')
      return h('div', {class: 'text-muted-foreground whitespace-nowrap'}, date)
    },
  },
  {
    id: 'actions',
    enableHiding: false,
    cell: ({ row }) => {
      const member = row.original
      return h('div', { class: 'relative' }, h(MembersListDropdown, {
        member,
      }))
    },
  }
])
</script>

<template>
  <DataTable :columns="columns" :data="members" />
</template>
